
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { isUnauthorizedPlatformApiError } from "@/services/platformApi/client";
import { getUserServiceProviderCount, getUserServiceProviders } from "@/services/providers/providerOwnerService";
import { useSubscription } from "@/hooks/subscription";
import { hasPendingPayment } from "@/hooks/subscription/checkoutHelpers";

export const useMobileMenuData = () => {
  const { user, isAdmin, loading } = useAuth();
  const { data: subscriptionStatus } = useSubscription();

  const isSubscribed = isAdmin || subscriptionStatus?.subscribed;

  const { data: userListingsCount = 0 } = useQuery({
    queryKey: ['mobileUserListingsCount', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      try {
        return await getUserServiceProviderCount(user.id);
      } catch (error) {
        if (!isUnauthorizedPlatformApiError(error)) {
          console.error("Error fetching user listings count:", error);
        }
        return 0;
      }
    },
    enabled: !loading && !!user,
  });

  const { data: userApprovedListings = [] } = useQuery({
    queryKey: ['mobileUserApprovedListings', user?.id],
    queryFn: async () => {
      if (!user) return [];
      try {
        const listings = await getUserServiceProviders(user.id, "approved");
        return listings.map(listing => ({ id: listing.id, name: listing.name }));
      } catch (error) {
        if (!isUnauthorizedPlatformApiError(error)) {
          console.error("Error fetching user approved listings:", error);
        }
        return [];
      }
    },
    enabled: !loading && !!user,
  });
  
  const { data: isPendingPayment = false } = useQuery({
    queryKey: ['mobilePendingPayment', user?.id],
    queryFn: async () => {
      if (!user) return false;
      return await hasPendingPayment();
    },
    enabled: !loading && !!user,
    refetchInterval: 30000,
  });

  const hasPendingVerification = subscriptionStatus?.status === 'requires_action' || isPendingPayment;
  const hasReachedFreeLimit = user && !subscriptionStatus?.subscribed && userListingsCount >= 1;
  const hasReachedWhatsAppLimit = user && subscriptionStatus?.tier === 'whatsapp_tier' && userListingsCount >= 1;
  const shouldShowUpgradeButton = hasReachedFreeLimit || hasReachedWhatsAppLimit || hasPendingVerification;

  const getFormattedTierName = () => {
    const tier = subscriptionStatus?.tier || 'free_tier';
    switch(tier) {
      case 'premium_tier': return 'Premium';
      case 'whatsapp_tier': return 'WhatsApp Verified';
      default: return 'Free';
    }
  };
  
  const getBadgeVariant = () => {
    const tier = subscriptionStatus?.tier || 'free_tier';
    switch(tier) {
      case 'premium_tier': return 'default';
      case 'whatsapp_tier': return 'secondary';
      default: return 'outline';
    }
  };
  
  const getReferralLink = () => {
    if (!user?.id) return '#';
    const referralUrl = `${window.location.origin}/auth?mode=signup&ref=${user.id}`;
    const text = encodeURIComponent(`I'm inviting you to join WhatsGood HK! Sign up using my link:`);
    return `https://wa.me/?text=${text}%20${encodeURIComponent(referralUrl)}`;
  };

  return {
    user,
    isAdmin,
    subscriptionStatus,
    userApprovedListings,
    hasPendingVerification,
    shouldShowUpgradeButton,
    getFormattedTierName,
    getBadgeVariant,
    getReferralLink,
  };
};
